import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MarginComponent } from 'design/margin';
import { FormControlComponent } from 'design/formControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { useServices } from 'hooks/useServices';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { ClientSearchFieldset } from 'containers/ClientSearchFieldset';
import { useClientSearchForm } from 'hooks/useClientSearchForm';
import { validateClientSearchFormValues } from 'utils/clientSearchFormUtils';
import {
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { normalizeURL } from 'utils/noramlizeURL';
import { MissingClientRequisites } from 'utils/errors';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';

type Props = {
  paymentId: number;
  onCancel(): void;
  onEdited(): void;
};

export const GiroPaymentManagerClientSearchScreen = (props: Props) => {
  const { paymentId, onCancel: cancel, onEdited } = props;

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const { attachClientToGiroPayment } = useServices();

  const {
    values: formValues,
    violations: formViolations,
    setViolations: setFormViolations,
    setTextValue,
    setClientValue,
  } = useClientSearchForm();

  const {
    giroPaymentManager: { clientSearchScreen: ownDictionary },
    inputValidationClientHasNoRequisites,
  } = useIntlDictionary();

  const changeSelection = React.useCallback(() => {
    setClientValue(null);
    setTextValue('');
  }, [setClientValue, setTextValue]);

  const hasClientNoRequisitesHint = formViolations.some(
    (violation) => violation.name === 'ClientHasNoRequisites'
  );

  const submit = React.useCallback(() => {
    setFormViolations([]);

    const violations = validateClientSearchFormValues(formValues);

    if (violations.length === 0 && formValues.client) {
      const promise = attachClientToGiroPayment(paymentId, formValues.client.id)
        .then(onEdited)
        .catch((error) => {
          if (error instanceof MissingClientRequisites) {
            setFormViolations([{ name: 'ClientHasNoRequisites' }]);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      Promise.resolve(violations).then(setFormViolations);
    }
  }, [
    attachClientToGiroPayment,
    formValues,
    injectPromise,
    onEdited,
    paymentId,
    setFormViolations,
  ]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={formViolations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            formValues.client === null ? (
              <FieldsetLayoutComponent>
                <ClientSearchFieldset
                  disabled={asyncActive}
                  values={formValues}
                  violations={formViolations}
                  onTextChange={setTextValue}
                  onClientChange={setClientValue}
                />
              </FieldsetLayoutComponent>
            ) : (
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent weight="bold" upperCase>
                    {ownDictionary.clientTable.titleText}
                  </TextComponent>
                  <TextComponent>
                    <ClickControlComponent onClick={changeSelection}>
                      <TextComponent color="accent">
                        {ownDictionary.clientTable.changeActionText}
                      </TextComponent>
                    </ClickControlComponent>
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.organizationNameLabel}
                  </TextComponent>
                  <TextComponent>{formValues.client.org.name}</TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.fullNameLabel}
                  </TextComponent>
                  <TextComponent>
                    {normalizeProfileLike(
                      formValues.client.person
                    ).toFullName()}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.unpLabel}
                  </TextComponent>
                  <TextComponent>{formValues.client.unp || '-'}</TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.prodStackLabel}
                  </TextComponent>
                  <TextComponent>
                    {formValues.client.prodStackInfo?.originURL
                      ? normalizeURL(
                          formValues.client.prodStackInfo.originURL
                        ).toHostName()
                      : '-'}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.idLabel}
                  </TextComponent>
                  <TextComponent>{formValues.client.id}</TextComponent>
                </SmallTableRowComponent>
                {hasClientNoRequisitesHint && (
                  <TextComponent color="critical">
                    {inputValidationClientHasNoRequisites}
                  </TextComponent>
                )}
              </SmallTableWrapperComponent>
            )
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
